import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Box, Stack, Typography, FormHelperText } from '@mui/material';
import { ShieldIcon, PlusIcon } from 'lucide-react';
import { Modal, Button, FieldLabel } from '@repo/ui';
import { SelectField, EmailChipInput } from 'components';
import { SELECT_FIELD_FOOTER_ROW_SX } from 'components/input/fields/SelectField';
import type { SelectItem } from 'components/input/fields/SelectField';
import { getMemberRoleDisplayName } from 'constants/roles';
import { useSnackbar } from 'contexts';
import { useInviteCompanyAdmins, useCompanyRoles } from 'hooks/api';
import { useReferrerState } from 'hooks';
import { ROUTES } from 'routes';

import { RolePermissionsInfo } from './RolePermissionsInfo';

interface InviteCompanyUserModalProps {
  open: boolean;
  onClose?: () => void;
  refetch?: () => void;
}

// Footer row inside the Role dropdown — same mechanism as `menuBottomButton` in
// @repo/ui MenuButton (see ViewsDropdown's "Save view" / "Reset to default"
// footer): a non-selectable row pinned below the scrollable option list.
//
// The style itself now lives on `SelectField` (§36 — Business figures' Role filter is
// the second site to want this exact row, and a second private copy is how the two
// drift). This alias keeps the call sites below unchanged.
const FOOTER_ROW_SX = SELECT_FIELD_FOOTER_ROW_SX;

export const InviteCompanyUserModal = (props: InviteCompanyUserModalProps) => {
  const { refetch, open, onClose } = props;
  const navigate = useNavigate();
  const { control, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      role: '',
      email_addresses: [] as string[],
    },
  });
  const selectedRoleName = watch('role');
  const { open: notification } = useSnackbar();

  // Same roles shown on the Roles page (built-in + custom), so the invite picker
  // matches it and can surface the Default / Custom badge.
  const { data: companyRoles = [], isLoading: isLoadingRoles } = useCompanyRoles();
  const roles = companyRoles;

  const [selectedRoleId, setSelectedRoleId] = useState<string>('');

  // Set default role to Company Admin when roles are loaded
  useEffect(() => {
    if (roles.length > 0 && !selectedRoleId) {
      const defaultRole = roles.find(role => role.name === 'company_admin');
      if (defaultRole) {
        setSelectedRoleId(defaultRole.id);
        setValue('role', defaultRole.name);
      }
    }
  }, [roles, selectedRoleId, setValue]);

  const { mutate: inviteAdmins, isPending } = useInviteCompanyAdmins({
    onSuccess: (data) => {
      // Robust count: prefer totalInvited, fall back to the returned list length
      // (guards against the response-shape drift that showed "undefined").
      const count = data?.totalInvited ?? data?.invitedAdmins?.length ?? 0;
      notification({
        type: 'success',
        title: `Successfully invited ${count} user${count === 1 ? '' : 's'}`,
      });

      handleClose();
      refetch?.();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error.message || 'Failed to invite admins';
      notification({
        type: 'error',
        title: message,
      });
    },
  });

  const handleInvite = handleSubmit((data) => {
    const emails = data.email_addresses || [];

    if (emails.length === 0) {
      notification({
        type: 'error',
        title: 'Please enter at least one valid email address',
      });
      return;
    }

    // Send invite with selected role
    inviteAdmins({
      emails,
      roleId: selectedRoleId || undefined,
    });
  });

  const handleClose = () => {
    reset();
    setSelectedRoleId('');
    onClose?.();
  };

  // Navigate to the Roles page — closes this modal first (precedent:
  // AssignRoleModal's "Create custom role" link).
  //
  // Both carry the referrer (§42) so Roles & permissions renders "← Back to
  // Users". This is a lateral jump between sibling Settings sections, not a
  // drill-in, so `ReferrerBackLink`'s ancestor check won't suppress it: neither
  // path contains the other. Without it the user lands on Roles mid-invite with
  // no way back to the list they started from — the exact case §42 exists for.
  const referrer = useReferrerState();

  const handleViewRoles = () => {
    handleClose();
    navigate(ROUTES.SETTINGS_COMPANY_ROLES, { state: referrer });
  };

  const handleAddCustomRole = () => {
    handleClose();
    navigate(`${ROUTES.SETTINGS_COMPANY_ROLES}?create=1`, { state: referrer });
  };

  const roleSelectFooter = (
    <Stack spacing="2px">
      <Box sx={FOOTER_ROW_SX} onClick={handleViewRoles}>
        <ShieldIcon size={16} strokeWidth={1.5} />
        <Typography variant="body3">View roles</Typography>
      </Box>
      <Box sx={FOOTER_ROW_SX} onClick={handleAddCustomRole}>
        <PlusIcon size={16} strokeWidth={1.5} />
        <Typography variant="body3">Add custom role</Typography>
      </Box>
    </Stack>
  );

  const roleOptions: SelectItem[] = React.useMemo(
    () =>
      roles.map(role => ({
        label: getMemberRoleDisplayName(role.name),
        value: role.name,
        rightLabel: role.isCustom ? 'Custom' : 'Default',
      })),
    [roles]
  );

  return (
    <Modal
      title="Invite users"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button disableElevation onClick={handleInvite} loading={isPending}>
            Invite
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Stack direction="column" gap={1}>
          {/* FieldLabel owns the label + 8px label→control gap (§31) — the same
              source SelectField's own field wrappers use elsewhere. Kept in its
              own Box (no extra gap) so the parent Stack's `gap` doesn't stack on
              top of FieldLabel's own mb. */}
          <Box>
            <FieldLabel label="Role" />
            <Controller
              name="role"
              control={control}
              rules={{ required: 'Role is required' }}
              render={({ field, fieldState }) => (
                <>
                  <SelectField
                    options={roleOptions}
                    value={field.value}
                    placeholder="Select a role"
                    disabled={isLoadingRoles || roleOptions.length === 0}
                    onChange={value => {
                      field.onChange(value);
                      const selectedRole = roles.find(role => role.name === value);
                      if (selectedRole) {
                        setSelectedRoleId(selectedRole.id);
                      }
                    }}
                    footer={roleSelectFooter}
                  />
                  {fieldState.error && (
                    <Typography variant="body2" color="error">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Box>
          <FormHelperText>
            Select the role for the invited users
          </FormHelperText>
          <RolePermissionsInfo roleName={selectedRoleName} />
        </Stack>
        <Stack direction="column" gap={1}>
          <Controller
            name="email_addresses"
            control={control}
            render={({ field }) => (
              <EmailChipInput
                label="Email addresses"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <FormHelperText>
            To invite multiple people separate the emails by comma, space or new line
          </FormHelperText>
        </Stack>
      </Stack>
    </Modal>
  );
};
